import React from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Typography,
    Alert,
    Grid,
    Paper,
    Tooltip
} from '@mui/material';
import { TimeSlot } from '../types/timeSlot';
import { format, isBefore, set } from 'date-fns';

interface TimeSlotPickerProps {
    timeSlots: TimeSlot[] | null;
    selectedTime: string | null;
    onTimeSelect: (time: string) => void;
    loading?: boolean;
    error?: string | null;
    isBookingView?: boolean;
    selectedDate?: Date | null;
}

const TimeSlotPicker: React.FC<TimeSlotPickerProps> = ({
    timeSlots,
    selectedTime,
    onTimeSelect,
    loading = false,
    error = null,
    isBookingView = false,
    selectedDate = null
}) => {
    const isSlotAvailable = (slot: TimeSlot): boolean => {
        if (!slot) return false;
        
        // Eğer seçili tarih varsa, geçmiş saatleri kontrol et
        if (selectedDate) {
            const now = new Date();
            const slotDateTime = set(new Date(selectedDate), {
                hours: parseInt(slot.time.split(':')[0]),
                minutes: parseInt(slot.time.split(':')[1]),
                seconds: 0,
                milliseconds: 0
            });
            
            // Eğer slot zamanı şimdiden önceyse, müsait değil
            if (isBefore(slotDateTime, now)) {
                return false;
            }
        }
        
        return slot.isActive && slot.status !== 'booked' && slot.paymentStatus === undefined;
    };

    const handleTimeSelect = (slot: TimeSlot) => {
        if (!slot) return;
        if (isSlotAvailable(slot)) {
            onTimeSelect(slot.time);
        }
    };

    const isTimeSelected = (slot: TimeSlot): boolean => {
        if (!slot || !selectedTime) return false;
        return selectedTime === slot.time;
    };

    const getSlotColor = (slot: TimeSlot): 'success' | 'error' => {
        if (!slot) return 'error';
        
        // Eğer seçili tarih varsa, geçmiş saatleri kontrol et
        if (selectedDate) {
            const now = new Date();
            const slotDateTime = set(new Date(selectedDate), {
                hours: parseInt(slot.time.split(':')[0]),
                minutes: parseInt(slot.time.split(':')[1]),
                seconds: 0,
                milliseconds: 0
            });
            
            // Eğer slot zamanı şimdiden önceyse, kırmızı göster
            if (isBefore(slotDateTime, now)) {
                return 'error';
            }
        }
        
        return isSlotAvailable(slot) ? 'success' : 'error';
    };

    const getSlotTooltip = (slot: TimeSlot): string => {
        if (!slot) return 'Geçersiz saat dilimi';
        
        // Eğer seçili tarih varsa, geçmiş saatleri kontrol et
        if (selectedDate) {
            const now = new Date();
            const slotDateTime = set(new Date(selectedDate), {
                hours: parseInt(slot.time.split(':')[0]),
                minutes: parseInt(slot.time.split(':')[1]),
                seconds: 0,
                milliseconds: 0
            });
            
            // Eğer slot zamanı şimdiden önceyse
            if (isBefore(slotDateTime, now)) {
                return 'Bu saat geçmiş';
            }
        }
        
        if (slot.status === 'booked') return 'Bu saat dolu';
        if (!slot.isActive) return 'Bu saat dolu';
        if (slot.paymentStatus) return 'Bu saat rezerve edilmiş';
        return 'Müsait';
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error" sx={{ mt: 2 }}>
                {error}
            </Alert>
        );
    }

    if (!selectedDate && isBookingView) {
        return (
            <Alert severity="info" sx={{ mt: 2 }}>
                Lütfen önce bir tarih seçin
            </Alert>
        );
    }

    if (!timeSlots || timeSlots.length === 0) {
        return (
            <Alert severity="info" sx={{ mt: 2 }}>
                {isBookingView ? 
                    'Seçilen tarih için müsait randevu saati bulunmamaktadır.' :
                    'Henüz randevu saati eklenmemiş.'
                }
            </Alert>
        );
    }

    return (
        <Box sx={{ mt: 2 }}>
            <Grid container spacing={1}>
                {timeSlots.map((slot, index) => {
                    if (!slot) return null;
                    
                    const isSelected = isTimeSelected(slot);
                    const color = getSlotColor(slot);
                    const isAvailable = isSlotAvailable(slot);
                    
                    return (
                        <Grid item xs={4} sm={3} md={2} key={index}>
                            <Tooltip title={getSlotTooltip(slot)} arrow>
                                <span>
                                    <Button
                                        variant="contained"
                                        color={color}
                                        onClick={() => handleTimeSelect(slot)}
                                        disabled={!isAvailable}
                                        fullWidth
                                        sx={{
                                            height: '40px',
                                            fontSize: '0.875rem',
                                            backgroundColor: isSelected ? 
                                                (color === 'error' ? 'error.dark' : 'success.dark') 
                                                : !isAvailable ? 'error.main' : undefined,
                                            '&:hover': {
                                                backgroundColor: isSelected ? 
                                                    (color === 'error' ? 'error.dark' : 'success.dark') 
                                                    : !isAvailable ? 'error.main' : undefined
                                            },
                                            '&.Mui-disabled': {
                                                backgroundColor: 'error.main',
                                                opacity: 1,
                                                color: '#fff'
                                            }
                                        }}
                                    >
                                        {slot.time}
                                    </Button>
                                </span>
                            </Tooltip>
                        </Grid>
                    );
                })}
            </Grid>
        </Box>
    );
};

export default TimeSlotPicker;